import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink } from 'react-router'
import { Menu, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import LanguageSelector from './LanguageSelector'

// Os rótulos vivem no catálogo (navbar.links.*); os slugs são rota, não texto,
// e são iguais em todas as línguas — é isso que permite ao seletor de língua
// trocar de idioma mantendo a página atual.
const NAV_LINKS = [
    { key: 'home', href: '/' },
    { key: 'trafico', href: '/trafico-de-seres-humanos-e-exploracao' },
    { key: 'oQueFazer', href: '/o-que-fazer' },
    { key: 'informacoesUteis', href: '/informacoes-uteis' },
    { key: 'direitos', href: '/direitos' },
    { key: 'apoio', href: '/apoio', pill: true },
]

// Símbolo compacto (o mesmo do favicon) — vive em public/ porque também é o ícone do site
const logoCompacto = '/Favicon_APAV.svg'

// A partir de quantos px de scroll o logótipo dá lugar ao símbolo compacto
// (padrão apav.pt / precisodeajuda / discriminacao)
const SCROLL_THRESHOLD = 60

export default function Navbar() {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [animationsPaused, setAnimationsPaused] = useState(false)
    const [scrolled, setScrolled] = useState(
        typeof window !== 'undefined' && window.scrollY > SCROLL_THRESHOLD
    )

    /* Em alemão ou ucraniano os seis rótulos são muito mais largos do que em
       português e transbordavam por cima do logótipo. Mede-se a largura real e,
       quando não cabem, passa-se ao hambúrguer também em desktop. O bloco dos
       links fica sempre no DOM (invisível e fora do fluxo quando não cabe) para
       a medida não oscilar. */
    const rowRef = useRef(null)
    const linksRef = useRef(null)
    const [naoCabe, setNaoCabe] = useState(false)

    useEffect(() => {
        const medir = () => {
            const row = rowRef.current
            const links = linksRef.current
            if (!row || !links) return
            const logo = row.firstElementChild
            const folga = 24
            setNaoCabe(logo.getBoundingClientRect().width + folga + links.scrollWidth > row.clientWidth)
        }
        medir()
        window.addEventListener('resize', medir)
        document.fonts?.ready.then(medir)
        // o painel de acessibilidade amplia o texto sem a janela mudar de tamanho
        const observer = new MutationObserver(medir)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] })
        return () => {
            window.removeEventListener('resize', medir)
            observer.disconnect()
        }
    }, [])

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD)
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    /* Respeitar "Pausar animações" (widget de acessibilidade) e prefers-reduced-motion */
    useEffect(() => {
        const checkMotion = () => {
            const isPaused = document.documentElement.classList.contains('a11y-pause-animations')
            const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            setAnimationsPaused(isPaused || prefersReduced)
        }
        checkMotion()
        const observer = new MutationObserver(checkMotion)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
        return () => observer.disconnect()
    }, [])

    const transitionProps = (duration = 0.3) =>
        animationsPaused ? { duration: 0 } : { duration, ease: 'easeOut' }

    return (
        <nav className="fixed w-full top-0 z-50 bg-white/95 backdrop-blur-xl shadow-xs" style={{ fontFamily: 'Rubik, system-ui, sans-serif' }} role="navigation" aria-label={t('navbar.aria')}>
            <div className="max-w-7xl mx-auto px-8 lg:px-16">
                {/* items-start so the logo overflows below the bar */}
                <div ref={rowRef} className="flex justify-between items-start h-16 lg:h-20">

                    {/* Logo — grande a transbordar da barra; ao fazer scroll passa ao
                        símbolo compacto (favicon), que cabe dentro da barra */}
                    <div className={`flex-shrink-0 flex h-16 lg:h-20 ${scrolled ? 'items-center' : 'items-start'}`}>
                        <Link
                            to="/"
                            className="hover:opacity-90 transition-opacity block focus-visible:ring-2 focus-visible:ring-navy rounded-lg"
                            aria-label={t('navbar.logoAria')}
                        >
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.img
                                    key={scrolled ? 'compacto' : 'completo'}
                                    src={scrolled ? logoCompacto : '/assets/images/logo_APAV-fundoazulescuro.svg'}
                                    alt={t('navbar.logoAlt')}
                                    initial={{ opacity: 0, scale: 0.85 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.85 }}
                                    transition={transitionProps(0.18)}
                                    className={
                                        scrolled
                                            ? 'h-11 lg:h-12 w-auto object-contain'
                                            : 'h-24 lg:h-28 w-auto object-contain'
                                    }
                                />
                            </AnimatePresence>
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div
                        className={`hidden lg:flex items-center h-16 lg:h-20 ${
                            naoCabe ? 'lg:invisible lg:absolute lg:end-0 lg:pointer-events-none' : ''
                        }`}
                        aria-hidden={naoCabe || undefined}
                    >
                        <div ref={linksRef} className="ml-10 flex items-center space-x-4">
                            {NAV_LINKS.map((link) =>
                                link.pill ? (
                                    <NavLink
                                        key={link.key}
                                        to={link.href}
                                        className={({ isActive }) =>
                                            `px-5 py-1.5 text-sm font-bold transition-all whitespace-nowrap rounded-full border-2 ${
                                                isActive
                                                    ? 'text-white'
                                                    : 'bg-white hover:opacity-80'
                                            }`
                                        }
                                        style={({ isActive }) => ({
                                            borderColor: '#00203F',
                                            backgroundColor: isActive ? '#00203F' : 'white',
                                            color: isActive ? 'white' : '#00203F',
                                        })}
                                    >
                                        {t(`navbar.links.${link.key}`)}
                                    </NavLink>
                                ) : (
                                    <NavLink
                                        key={link.key}
                                        to={link.href}
                                        end={link.href === '/'}
                                        className={({ isActive }) =>
                                            `px-1 py-2 text-sm font-bold transition-all whitespace-nowrap border-b-2 ${
                                                isActive
                                                    ? 'border-navy'
                                                    : 'border-transparent hover:border-navy/40'
                                            }`
                                        }
                                        style={{ color: '#00203F' }}
                                    >
                                        {t(`navbar.links.${link.key}`)}
                                    </NavLink>
                                )
                            )}
                            <LanguageSelector />
                        </div>
                    </div>

                    {/* Mobile hamburger */}
                    <div className={`${naoCabe ? '' : 'lg:hidden '}self-center`}>
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="p-2 rounded-md focus-visible:ring-2 focus-visible:ring-navy"
                            style={{ color: '#00203F' }}
                            aria-label={isOpen ? t('navbar.closeMenu') : t('navbar.openMenu')}
                            aria-expanded={isOpen}
                            aria-controls={isOpen ? 'menu-mobile' : undefined /* fechado, o id não existe no DOM */}
                        >
                            {isOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        id="menu-mobile"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={transitionProps(0.25)}
                        className={`${naoCabe ? '' : 'lg:hidden '}border-t border-slate-100 overflow-hidden bg-white`}
                    >
                        <div className="px-4 pt-2 pb-6 space-y-1 max-h-[80vh] overflow-y-auto">
                            {NAV_LINKS.map((link) =>
                                link.pill ? (
                                    <NavLink
                                        key={link.key}
                                        to={link.href}
                                        className={({ isActive }) =>
                                            `block mx-4 my-2 px-4 py-3 text-base font-bold rounded-full border-2 text-center transition-colors ${
                                                isActive ? 'text-white' : 'bg-white'
                                            }`
                                        }
                                        style={({ isActive }) => ({
                                            borderColor: '#00203F',
                                            backgroundColor: isActive ? '#00203F' : 'white',
                                            color: isActive ? 'white' : '#00203F',
                                        })}
                                        onClick={() => setIsOpen(false)}
                                    >
                                        {t(`navbar.links.${link.key}`)}
                                    </NavLink>
                                ) : (
                                    <NavLink
                                        key={link.key}
                                        to={link.href}
                                        end={link.href === '/'}
                                        className={({ isActive }) =>
                                            `block px-4 py-4 text-lg font-bold border-b border-slate-100 border-s-4 transition-colors ${
                                                isActive ? 'bg-slate-100 border-s-navy' : 'border-s-transparent'
                                            }`
                                        }
                                        style={{ color: '#00203F' }}
                                        onClick={() => setIsOpen(false)}
                                    >
                                        {t(`navbar.links.${link.key}`)}
                                    </NavLink>
                                )
                            )}
                            <div className="pt-3 border-t border-slate-100 mt-3">
                                <LanguageSelector variant="inline" />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    )
}
